import { useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { sendResetPassword, setIsButtonDisabled } from '../../slices/auth.slice';
import { RootState } from '../../store';
import { useAppDispatch } from '../../store/hooks';
import ResetPasswordWrapper from './ResetPassword';
import styles from './ResetPassword.module.scss';

const ResetPassword = () => {
  const [qParams] = useSearchParams();
  const token = qParams.get('token');

  const dispatch = useAppDispatch();
  const { isButtonDisabled } = useSelector((state: RootState) => state.auth);


  const oneDigit = useRef(0);
  const oneUppercase = useRef(0);
  const eightNumber = useRef(0);
  const oneSpecialCharacter = useRef(0);
  const fourteenNumber = useRef(0);
  const oneLowercase = useRef(0);

  // states for the input fields.
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  // for adding the error message for the input fields.
  const [inputError, setInputError] = useState({
    passwordError: false,
    confirmPasswordError: false,
  });

  // for adding the error message.
  const [errorMessage, setErrorMessage] = useState(false);

  const [toggleIcon, setToggleIcon] = useState({
    passwordIcon: false,
    confirmPasswordIcon: false,
  });
  const [passwordMatch, setPasswordMatch] = useState({
    passwordMatchError: false,
    confirmPasswordMatchError: false,
  });

  const hasANumber = (value: string) => {
    return Array.prototype.some.call(value, c => !isNaN(c) && c.trim());
  };
  const hasUppercaseCharacter = (value: string) => {
    return /[A-Z]/.test(value);
  };
  const hasLowercaseCharacter = (value: string) => {
    return /[a-z]/.test(value);
  };

  const hasEightNumber = (value: string) => {
    return value.length >= 8;
  };

  const hasFourteenNumber = (value: string) => {
    return value.length <= 16 && value !== '';
  };
  const hasSpecialCharacter = (value: string) => {
    return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(value);
  };

  const setIndicatorColor = (ref: any, condition: any) => {
    ref.current.style.color = condition ? 'green' : '#95969D';
  };

  // Helper functions for validation
  const validatePassword = (value: string) => {
    const hasDigit = hasANumber(value);
    const hasUppercase = hasUppercaseCharacter(value);
    const hasEightDigits = hasEightNumber(value);
    const hasSpecialChar = hasSpecialCharacter(value);
    const hasFourteenDigits = hasFourteenNumber(value);
    const hasLowercase = hasLowercaseCharacter(value);

    setIndicatorColor(oneDigit, hasDigit);
    setIndicatorColor(oneUppercase, hasUppercase);
    setIndicatorColor(eightNumber, hasEightDigits);
    setIndicatorColor(oneSpecialCharacter, hasSpecialChar);
    setIndicatorColor(fourteenNumber, hasFourteenDigits);
    setIndicatorColor(oneLowercase, hasLowercase);

    return hasDigit && hasUppercase && hasEightDigits && hasSpecialChar && hasFourteenDigits && hasLowercase;
  };

  const confirmPasswordValidation = (value: string) => {
    if (value === formData.password && value.length !== 0 && validatePassword(value)) {
      dispatch(setIsButtonDisabled(false));
      setPasswordMatch({
        ...passwordMatch,
        passwordMatchError: false,
        confirmPasswordMatchError: false,
      });
    } else if (value === formData.password) {
      setPasswordMatch({
        ...passwordMatch,
        confirmPasswordMatchError: false,
      });
    } else {
      dispatch(setIsButtonDisabled(true));
      setPasswordMatch({
        ...passwordMatch,
        confirmPasswordMatchError: formData.password !== '',
      });
    }
  };

  /**
   * Handles the input change event for the form fields and performs validation checks.
   * @param {Event} e - The input change event object.
   * @returns None
   */
  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    const updatedInputError = { ...inputError };

    // Password matching validation
    const validatePasswordMatch = () => {
      if (name === 'confirmPassword') {
        confirmPasswordValidation(value);
      } else if (name === 'password' && value !== formData.confirmPassword && formData.confirmPassword !== '') {
        dispatch(setIsButtonDisabled(true));
        setPasswordMatch({
          ...passwordMatch,
          passwordMatchError: true,
        });
      } else {
        if (name === 'password' && value === '') {
          dispatch(setIsButtonDisabled(true));
        } else if (name === 'password' && value === formData.confirmPassword) {
          dispatch(setIsButtonDisabled(false));
        }
        setPasswordMatch({
          ...passwordMatch,
          passwordMatchError: false,
          confirmPasswordMatchError: false,
        });
      }
    };

    // Validate password and password matching
    if (name === 'password') {
      validatePassword(value);
      validatePasswordMatch();
    } else {
      validatePasswordMatch();
    }

    // Set error state
    setInputError(updatedInputError);
    // Set form data
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));

    // Reset general error message
    setErrorMessage(false);
  };

  // modal open function
  const onSubmit = () => {
    const resetPasswordPayload = {
      password: formData.confirmPassword,
      token,
      tokenCode: '',
    };
    dispatch(sendResetPassword(resetPasswordPayload));
  };

  // function for toggling the password show/hide.
  const togglePasswordIcon = () => {
    setToggleIcon({
      ...toggleIcon,
      passwordIcon: !toggleIcon.passwordIcon,
    });
  };

  /**
   * Toggles the visibility of the confirm password icon by updating the state.
   * @returns None
   */
  const toggleConfirmPasswordIcon = () => {
    setToggleIcon({
      ...toggleIcon,
      confirmPasswordIcon: !toggleIcon.confirmPasswordIcon,
    });
  };

  return (
    <div className={styles.componentWrapper}>
      <ResetPasswordWrapper
        logoUrl={''}
        handleEmailModal={onSubmit}
        formData={formData}
        handleInputChange={handleInputChange}
        errorMessage={errorMessage}
        inputError={inputError}
        toggleIcon={toggleIcon}
        togglePasswordIcon={togglePasswordIcon}
        passwordMatch={passwordMatch}
        oneDigit={oneDigit}
        oneUppercase={oneUppercase}
        oneSpecialCharacter={oneSpecialCharacter}
        eightNumber={eightNumber}
        fourteenNumber={fourteenNumber}
        toggleConfirmPasswordIcon={toggleConfirmPasswordIcon}
        oneLowercase={oneLowercase}
        buttonDisabled={isButtonDisabled}
      />
    </div>
  );
};

export default ResetPassword;
